import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Header from '../components/Header';
import SinapiComposicaoModal from '../components/SinapiComposicaoModal';
import useSinapiSearch from '../hooks/useSinapiSearch';
import useOrcamentoSinapi from '../hooks/useOrcamentoSinapi';
import estadosBrasil from '../data/estadosBrasil';
import LogService, { LOG_ACTIONS } from '../services/LogService';
import Footer from '../components/Footer';

function formatCurrency(value) {
  if (value === null || value === undefined) return '-';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

const TIPO_PRECO_LABELS = {
  NAO_DESONERADO: 'Não Desonerado',
  DESONERADO: 'Desonerado',
  SEM_ENCARGOS: 'Sem Encargos',
};

const SINAPI_GRUPOS_LIST = [
  'Serviços Preliminares', 'Fundações', 'Estrutura', 'Paredes e Painéis',
  'Coberturas', 'Impermeabilização', 'Revestimentos', 'Pisos',
  'Esquadrias', 'Vidros', 'Pinturas', 'Instalações Elétricas',
  'Instalações Hidráulicas', 'Instalações de Esgoto', 'Instalações de Gás',
  'Instalações de Incêndio', 'Pavimentação', 'Drenagem', 'Terraplanagem', 'Outros',
];

function OrcamentoSinapi() {
  const navigate = useNavigate();
  const [selectedCodigo, setSelectedCodigo] = useState(null);
  const [searchTab, setSearchTab] = useState('composicoes');

  const search = useSinapiSearch(searchTab);
  const orcamento = useOrcamentoSinapi();

  const handleAddFromModal = (item) => {
    orcamento.addItem(item);
    LogService.log(LOG_ACTIONS.SINAPI_ADD_TO_ORCAMENTO, { details: { codigo: item.codigo } });
  };

  const handleExportExcel = () => {
    const wsData = [
      ['Código', 'Descrição', 'Unidade', 'Preço Unitário', 'Quantidade', 'Subtotal'],
      ...orcamento.items.map((item) => [
        item.codigo,
        item.descricao,
        item.unidade,
        item.preco || 0,
        item.quantidade,
        (item.preco || 0) * item.quantidade,
      ]),
      [],
      ['', '', '', '', 'Subtotal', orcamento.subtotal],
      ['', '', '', '', `BDI (${orcamento.bdi}%)`, orcamento.bdiValor],
      ['', '', '', '', 'TOTAL', orcamento.total],
      [],
      [`Referência SINAPI - ${TIPO_PRECO_LABELS[search.tipoPreco]} - Estado: ${search.estado}`],
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [
      { wch: 15 }, { wch: 50 }, { wch: 8 }, { wch: 15 }, { wch: 12 }, { wch: 15 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Orçamento SINAPI');
    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `QuantoCustaObra_SINAPI_${new Date().toISOString().slice(0, 10)}.xlsx`);
    LogService.log(LOG_ACTIONS.SINAPI_EXPORT_EXCEL, { details: { items: orcamento.items.length, total: orcamento.total } });
  };

  return (
    <div className="min-h-screen bg-muted/30">
      <Header />
      <main className="max-w-6xl mx-auto px-4 py-6 md:py-10 pt-36">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-1">Orçamento SINAPI</h1>
            <p className="text-muted-foreground">Busque composições e insumos SINAPI para montar seu orçamento</p>
          </div>
          <button
            onClick={() => navigate('/orcamento-cub')}
            className="mt-3 sm:mt-0 px-4 py-2 text-sm font-medium text-accent bg-accent/10 border border-accent/20 rounded-lg hover:bg-accent/20 cursor-pointer transition-colors"
          >
            Orçamento CUB
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Search panel */}
          <div className="lg:col-span-2 space-y-4">
            {/* Search tabs */}
            <div className="flex gap-2 mb-2">
              <button
                onClick={() => setSearchTab('composicoes')}
                className={`px-4 py-2 text-sm font-medium rounded-lg cursor-pointer transition-colors ${
                  searchTab === 'composicoes' ? 'bg-accent text-white' : 'bg-card text-muted-foreground border border-border hover:bg-muted'
                }`}
              >
                Composições
              </button>
              <button
                onClick={() => setSearchTab('insumos')}
                className={`px-4 py-2 text-sm font-medium rounded-lg cursor-pointer transition-colors ${
                  searchTab === 'insumos' ? 'bg-accent text-white' : 'bg-card text-muted-foreground border border-border hover:bg-muted'
                }`}
              >
                Insumos
              </button>
            </div>

            {/* Filters */}
            <div className="bg-card rounded-xl shadow-card p-4">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Estado</label>
                  <select
                    value={search.estado}
                    onChange={(e) => search.setEstado(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-muted text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                  >
                    {estadosBrasil.map((e) => (
                      <option key={e.sigla} value={e.sigla}>{e.sigla} - {e.nome}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Regime</label>
                  <select
                    value={search.tipoPreco}
                    onChange={(e) => search.setTipoPreco(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-muted text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                  >
                    <option value="NAO_DESONERADO">Não Desonerado</option>
                    <option value="DESONERADO">Desonerado</option>
                    <option value="SEM_ENCARGOS">Sem Encargos</option>
                  </select>
                </div>
                {searchTab === 'composicoes' && (
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Grupo</label>
                    <select
                      value={search.grupo}
                      onChange={(e) => search.setGrupo(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-border bg-muted text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                    >
                      <option value="">Todos</option>
                      {SINAPI_GRUPOS_LIST.map((g) => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className={searchTab === 'insumos' ? 'sm:col-span-2' : ''}>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Buscar</label>
                  <input
                    type="text"
                    value={search.query}
                    onChange={(e) => search.setQuery(e.target.value)}
                    placeholder="Código ou descrição..."
                    className="w-full px-3 py-2 rounded-lg border border-border bg-muted text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Results table */}
            <div className="bg-card rounded-xl shadow-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted text-left">
                      <th className="px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Código</th>
                      <th className="px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Descrição</th>
                      <th className="px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Unid</th>
                      <th className="px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider text-right">Preço</th>
                      <th className="px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider text-center w-16"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {search.loading ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-12 text-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent mx-auto" />
                        </td>
                      </tr>
                    ) : search.results.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-12 text-center text-muted-foreground">
                          {search.query ? 'Nenhum resultado encontrado' : 'Digite para buscar composições ou insumos SINAPI'}
                        </td>
                      </tr>
                    ) : (
                      search.results.map((item) => (
                        <tr key={item.codigo} className="hover:bg-muted transition-colors">
                          <td className="px-4 py-3">
                            <button
                              onClick={() => setSelectedCodigo(item.codigo)}
                              className="text-accent hover:text-secondary font-medium cursor-pointer"
                            >
                              {item.codigo}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-foreground max-w-[300px] truncate">{item.descricao}</td>
                          <td className="px-4 py-3 text-muted-foreground">{item.unidade}</td>
                          <td className="px-4 py-3 text-foreground text-right font-medium">
                            {formatCurrency(item.preco)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => {
                                orcamento.addItem({
                                  codigo: item.codigo,
                                  descricao: item.descricao,
                                  unidade: item.unidade,
                                  preco: item.preco,
                                  quantidade: 1,
                                });
                                LogService.log(LOG_ACTIONS.SINAPI_ADD_TO_ORCAMENTO, { details: { codigo: item.codigo } });
                              }}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg cursor-pointer transition-colors"
                              title="Adicionar ao orçamento"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {search.pagination.totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted">
                  <span className="text-xs text-muted-foreground">
                    {search.pagination.total} resultados
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => search.goToPage(search.pagination.page - 1)}
                      disabled={search.pagination.page <= 1}
                      className="px-3 py-1.5 text-xs font-medium text-foreground bg-card border border-border rounded-lg hover:bg-muted cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      Anterior
                    </button>
                    <span className="px-3 py-1.5 text-xs text-muted-foreground">
                      {search.pagination.page} de {search.pagination.totalPages}
                    </span>
                    <button
                      onClick={() => search.goToPage(search.pagination.page + 1)}
                      disabled={search.pagination.page >= search.pagination.totalPages}
                      className="px-3 py-1.5 text-xs font-medium text-foreground bg-card border border-border rounded-lg hover:bg-muted cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      Próximo
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Budget panel */}
          <div className="space-y-4">
            <div className="bg-card rounded-xl shadow-card p-4">
              <h2 className="text-lg font-bold text-foreground mb-4">Meu Orçamento</h2>

              {/* Items list */}
              {orcamento.items.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  Adicione itens ao orçamento pela busca
                </p>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto mb-4">
                  {orcamento.items.map((item) => (
                    <div key={item.codigo} className="bg-muted rounded-lg p-3">
                      <div className="flex items-start justify-between mb-1">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-accent">{item.codigo}</p>
                          <p className="text-xs text-muted-foreground truncate">{item.descricao}</p>
                        </div>
                        <button
                          onClick={() => {
                            orcamento.removeItem(item.codigo);
                            LogService.log(LOG_ACTIONS.SINAPI_REMOVE_FROM_ORCAMENTO, { details: { codigo: item.codigo } });
                          }}
                          className="p-1 text-red-400 hover:text-red-600 cursor-pointer"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={item.quantidade}
                          onChange={(e) => orcamento.updateQuantidade(item.codigo, parseFloat(e.target.value) || 0.01)}
                          className="w-16 px-2 py-1 text-xs rounded border border-border bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-accent"
                        />
                        <span className="text-xs text-muted-foreground">{item.unidade}</span>
                        <span className="text-xs text-muted-foreground">x {formatCurrency(item.preco)}</span>
                        <span className="ml-auto text-xs font-bold text-foreground">
                          {formatCurrency((item.preco || 0) * item.quantidade)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* BDI */}
              <div className="border-t border-border pt-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-foreground">BDI</label>
                  <span className="text-sm font-bold text-accent">{orcamento.bdi}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="50"
                  value={orcamento.bdi}
                  onChange={(e) => orcamento.setBdi(parseInt(e.target.value))}
                  className="w-full accent-accent"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>0%</span>
                  <span>25%</span>
                  <span>50%</span>
                </div>
              </div>

              {/* Totals */}
              <div className="space-y-2 border-t border-border pt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-medium text-foreground">{formatCurrency(orcamento.subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">BDI ({orcamento.bdi}%)</span>
                  <span className="font-medium text-foreground">{formatCurrency(orcamento.bdiValor)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-border">
                  <span className="text-foreground">Total</span>
                  <span className="text-accent">{formatCurrency(orcamento.total)}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 mt-4">
                <button
                  onClick={handleExportExcel}
                  disabled={orcamento.items.length === 0}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Exportar Excel
                </button>
                <button
                  onClick={orcamento.clearAll}
                  disabled={orcamento.items.length === 0}
                  className="px-4 py-2.5 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Limpar
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Modal */}
      {selectedCodigo && (
        <SinapiComposicaoModal
          codigo={selectedCodigo}
          estado={search.estado}
          tipoPreco={search.tipoPreco}
          onClose={() => setSelectedCodigo(null)}
          onAdd={handleAddFromModal}
        />
      )}

      <Footer />
    </div>
  );
}

export default OrcamentoSinapi;
